"use client"

import { useRef } from "react"

import { accountText, type LocaleProps } from "../../personal-web"
import type { DeviceKey, DeviceOs } from "../types"

type Tab = {
  os: DeviceOs
  labelKey: string
}

const TABS: Tab[] = [
  { os: "ios", labelKey: "install.tab.ios" },
  { os: "android", labelKey: "install.tab.android" },
]

// Source: Simple Icons (CC0). Real Apple logo so users instantly recognise
// the iOS tab. Trademark belongs to Apple Inc.; we use it nominatively to
// label compatibility, same as Airalo / Holafly / Saily etc.
function AppleGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden
    >
      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  )
}

// Source: Simple Icons (CC0). The Android "Bugdroid" mark; trademark of
// Google LLC, used nominatively to label compatibility.
function AndroidGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden
    >
      <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4483-.9993.9993-.9993c.5511 0 .9993.4483.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4483-.9993.9993-.9993c.5511 0 .9993.4483.9993.9993 0 .5511-.4483.9997-.9993.9997m11.4045-6.02 1.9973-3.4592a.416.416 0 0 0-.1521-.5676.416.416 0 0 0-.5676.1521l-2.0223 3.503C15.5902 8.2439 13.8533 7.8508 12 7.8508s-3.5902.3931-5.1367 1.1009L4.841 5.4487a.4161.4161 0 0 0-.5677-.1521.4157.4157 0 0 0-.1521.5676l1.9973 3.4592C2.6889 11.1867.3432 14.6589 0 18.761h24c-.3432-4.1021-2.6889-7.5743-6.1185-9.4396" />
    </svg>
  )
}

type Props = LocaleProps & {
  /** Currently selected DeviceKey — used to derive which OS pill is active. */
  device: DeviceKey
  /** Called with the new OS choice; brand selection is reset to default. */
  onChange: (os: DeviceOs) => void
}

/**
 * Top-level iOS / Android segmented control. Segmented (not underlined tabs)
 * because the choice is mutually exclusive and the touch target is more
 * obvious on mobile. 48px min-height to satisfy WCAG touch sizing.
 */
export function DeviceTabs({ lng, device, onChange }: Props) {
  const currentOs: DeviceOs = device === "ios" ? "ios" : "android"
  const refs = useRef<Record<DeviceOs, HTMLButtonElement | null>>({
    ios: null,
    android: null,
  })

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Home" && e.key !== "End") return
    e.preventDefault()
    const nextOs: DeviceOs =
      e.key === "ArrowLeft" || e.key === "End"
        ? currentOs === "ios" ? "android" : "ios"
        : currentOs === "ios" ? "android" : "ios"
    onChange(nextOs)
    refs.current[nextOs]?.focus()
  }

  return (
    <div
      role="tablist"
      aria-label={accountText(lng, "install.tab.ariaLabel")}
      onKeyDown={onKeyDown}
      className="flex w-full items-stretch gap-1 rounded-2xl bg-stone-100 p-1"
    >
      {TABS.map((tab) => {
        const active = tab.os === currentOs
        return (
          <button
            key={tab.os}
            ref={(el) => {
              refs.current[tab.os] = el
            }}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(tab.os)}
            className={`flex flex-1 min-h-12 items-center justify-center gap-2 rounded-xl text-sm font-bold transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-500 ${
              active
                ? "bg-white text-orange-600 shadow-[0_2px_8px_-2px_rgba(28,25,23,0.12)]"
                : "bg-transparent text-stone-600 hover:text-stone-800"
            }`}
          >
            {tab.os === "ios" ? (
              <AppleGlyph className="h-[18px] w-[18px]" />
            ) : (
              <AndroidGlyph className="h-[18px] w-[18px]" />
            )}
            <span>{accountText(lng, tab.labelKey)}</span>
          </button>
        )
      })}
    </div>
  )
}
